// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet } from 'react-native';
import { Stack } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import appColors from '@/theme/colors';

/**
 * Every figure is independently nullable, and null means the service that owns it did not answer
 * — never zero. The page renders those as a dash rather than a number nobody should act on.
 */
type AdminStats = {
  /** 18-hole rounds not organised by one of our own accounts. */
  roundsPlayed: number | null;
  /** The same count without the admin exclusion, so the page can say how much of it is ours. */
  totalRoundsPlayed: number | null;
  totalCourses: number | null;
  /** Mostly OSM-import stubs no player can pick; shown as a note under the catalogue size. */
  disabledCourses: number | null;
  analysedCourses: number | null;
  totalUsers: number | null;
  claimedPlayers: number | null;
};

/**
 * Admin-only platform statistics (see AdminMenu in app/layout/RootLayoutNav.tsx): how much real
 * play, how big the course catalogue is and how much of it has been analysed, and how many
 * accounts and claimed guests exist.
 */
export default function AdminStatisticsScreen() {
  const { t, locale } = useI18n();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const requestIdRef = useRef<string | null>(null);

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  // A flag rather than a translated string: keeping the message out of state keeps `t` out of
  // the request effect, which would otherwise re-fire on every locale-provider render.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isAdmin || !user?.token) {
      setLoading(false);
      return;
    }

    const socket = socketRef.current;
    socket.setAuthToken(user.token);

    const requestId = createRequestId();
    requestIdRef.current = requestId;
    setLoading(true);
    setFailed(false);
    socket.send({ type: 'admin.stats.request', requestId });

    const unsubscribe = socket.onMessage((message) => {
      const payload = message as { type?: string; requestId?: string };
      if (payload.requestId !== requestIdRef.current) {
        return;
      }
      if (payload.type === 'admin.stats.ack') {
        const ack = message as Partial<AdminStats>;
        setStats({
          roundsPlayed: ack.roundsPlayed ?? null,
          totalRoundsPlayed: ack.totalRoundsPlayed ?? null,
          totalCourses: ack.totalCourses ?? null,
          disabledCourses: ack.disabledCourses ?? null,
          analysedCourses: ack.analysedCourses ?? null,
          totalUsers: ack.totalUsers ?? null,
          claimedPlayers: ack.claimedPlayers ?? null,
        });
        setLoading(false);
      } else if (payload.type === 'admin.stats.error') {
        setFailed(true);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isAdmin, user?.token]);

  const formatCount = (value: number | null) =>
    value === null ? '—' : value.toLocaleString(locale);

  return (
    <>
      <Stack.Screen options={{ title: t('adminStatisticsPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('adminStatisticsPage.title')}</Text>

        {!isAdmin ? (
          <Text style={styles.paragraph}>{t('adminStatisticsPage.forbidden')}</Text>
        ) : loading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" />
            <Text style={styles.paragraph}>{t('adminStatisticsPage.loading')}</Text>
          </View>
        ) : failed || !stats ? (
          <Text style={styles.errorText}>{t('adminStatisticsPage.error')}</Text>
        ) : (
          <>
            <StatCard
              testID="admin-stats-rounds-played"
              label={t('adminStatisticsPage.roundsPlayed')}
              value={formatCount(stats.roundsPlayed)}
              hint={
                stats.roundsPlayed === null
                  ? t('adminStatisticsPage.roundsPlayedUnknown')
                  : stats.totalRoundsPlayed === null
                    ? undefined
                    : t('adminStatisticsPage.roundsPlayedHint', {
                        total: formatCount(stats.totalRoundsPlayed),
                      })
              }
            />
            <StatCard
              testID="admin-stats-courses"
              label={t('adminStatisticsPage.courses')}
              value={formatCount(stats.totalCourses)}
              hint={
                stats.disabledCourses === null
                  ? undefined
                  : t('adminStatisticsPage.coursesHint', {
                      disabled: formatCount(stats.disabledCourses),
                    })
              }
            />
            <StatCard
              testID="admin-stats-courses-analysed"
              label={t('adminStatisticsPage.coursesAnalysed')}
              value={formatCount(stats.analysedCourses)}
            />
            <StatCard
              testID="admin-stats-users"
              label={t('adminStatisticsPage.users')}
              value={formatCount(stats.totalUsers)}
            />
            <StatCard
              testID="admin-stats-claimed-players"
              label={t('adminStatisticsPage.claimedPlayers')}
              value={formatCount(stats.claimedPlayers)}
              hint={t('adminStatisticsPage.claimedPlayersHint')}
            />
          </>
        )}
      </ScrollView>
    </>
  );
}

type StatCardProps = {
  testID: string;
  label: string;
  value: string;
  hint?: string;
};

const StatCard = ({ testID, label, value, hint }: StatCardProps) => (
  <View style={styles.card} testID={testID}>
    <Text style={styles.cardLabel}>{label}</Text>
    <Text style={styles.cardValue}>{value}</Text>
    {hint ? <Text style={styles.cardHint}>{hint}</Text> : null}
  </View>
);

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  errorText: {
    fontSize: 14,
    color: appColors.status.dangerText,
  },
  card: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 8,
    padding: 12,
    gap: 4,
  },
  cardLabel: {
    fontSize: 13,
    fontWeight: '700',
  },
  cardValue: {
    fontSize: 28,
    fontWeight: '700',
  },
  cardHint: {
    fontSize: 12,
    opacity: 0.7,
  },
});
